'use client';

import {
  Building2,
  Mail,
  Phone,
  Calendar,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { CustomerResult } from '../types';

function formatCurrency(value?: number): string {
  if (value === undefined) return '';

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  } catch {
    return '';
  }
}

function StatusBadge({ status }: { status?: CustomerResult['metadata']['status'] }) {
  if (!status) return null;

  const statusConfig = {
    active: {
      icon: CheckCircle,
      label: 'Active',
      className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    },
    inactive: {
      icon: AlertCircle,
      label: 'Inactive',
      className: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    },
    prospect: {
      icon: Clock,
      label: 'Prospect',
      className: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn('text-xs gap-1', config.className)}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
}

export const CustomerTile = createTile<CustomerResult>(
  'CustomerTile',
  (result) => {
    const { metadata } = result;
    const displayName = result.title || metadata.companyName || 'Unknown Customer';

    const renderProps: BaseTileRenderProps = {
      icon: metadata.companyName ? Building2 : User,
      iconColor: 'text-blue-400',
      title: displayName,
      subtitle: metadata.email,
      preview: result.preview,
      metadata: [],
      tags: [],
      rightContent: (
        <div className="flex items-center gap-2">
          {metadata.accountValue !== undefined && (
            <span className="text-sm font-medium text-blue-400">
              {formatCurrency(metadata.accountValue)}
            </span>
          )}
          <StatusBadge status={metadata.status} />
        </div>
      ),
    };

    if (metadata.email) {
      renderProps.metadata!.push({
        label: 'Email',
        value: metadata.email,
        icon: Mail,
      });
    }

    if (metadata.phone) {
      renderProps.metadata!.push({
        label: 'Phone',
        value: metadata.phone,
        icon: Phone,
      });
    }

    if (metadata.lastInteraction) {
      renderProps.metadata!.push({
        label: 'Last Contact',
        value: formatRelativeTime(metadata.lastInteraction),
        icon: Calendar,
      });
    }

    return renderProps;
  }
);

export default CustomerTile;
